/**
 * Cart Custom Fee PVC Processor - 购物车定制费PVC工艺处理器
 * PVC贴片工艺的独立实现
 */

// PVC_PATCH 工艺处理器
class PVCProcessor extends CraftProcessor {
  constructor() {
    super({
      processType: 'PVC_PATCH',
      productHandle: 'pvc-patch-fee',
      productTitle: 'PVC Patch Fee',
      ranges: [
        { min: 1, max: 11, variantId: 46024057061529 },
        { min: 12, max: 47, variantId: 46024057094297 },
        { min: 48, max: 143, variantId: 46024057127065 },
        { min: 144, max: 287, variantId: 46024057159833 },
        { min: 288, max: Infinity, variantId: 46024057192601 }
      ]
    });
  }
  
  /**
   * PVC工艺核心业务逻辑 - 计算所需的定制费用项
   * @param {Array} customProducts - 定制商品列表 [{quantity, customSides, draftOrderId, product}]
   * @returns {Array} - 需要添加的费用项列表
   */
  calculateRequiredFeeItems(customProducts) {
    if (!customProducts || customProducts.length === 0) {
      return [];
    }

    console.log('[PVC Processor] 开始计算PVC定制费用', { customProducts });
    
    // 详细检查输入数据
    customProducts.forEach((product, index) => {
      console.log(`[PVC Processor] 🔍 商品${index + 1}详细信息:`, {
        quantity: product.quantity,
        customSides: product.customSides,
        customSidesType: typeof product.customSides,
        customSidesLength: product.customSides?.length,
        groupId: product.groupId,
        draftOrderId: product.draftOrderId,
        productTitle: product.product?.title
      });
    });
    
    // 按group_id分组处理
    const groupedByGroup = this.groupByGroupId(customProducts);
    console.log('[PVC Processor] 按group_id分组:', groupedByGroup);
    
    // 为每个group独立计算费用项
    const allFeeItems = [];
    
    Object.entries(groupedByGroup).forEach(([groupId, products]) => {
      console.log(`[PVC Processor] 处理group: ${groupId}`, products);
      
      // 计算该group的统计信息
      const groupStats = this.calculateGroupStats(products);
      console.log(`[PVC Processor] group${groupId}统计:`, groupStats);
      
      // 根据该group的数量确定价格区间
      const priceRange = this.getPriceRangeByQuantity(groupStats.totalQuantity);
      console.log(`[PVC Processor] 💰 group${groupId}价格区间:`, priceRange);
      
      // 计算该group的实际收费数量
      const billingQuantity = this.calculateBillingQuantity(groupStats.totalQuantity, priceRange);
      console.log(`[PVC Processor] 🧮 group${groupId}计费数量: 实际${groupStats.totalQuantity} → 计费${billingQuantity}`);
      
      // 为该group生成费用项
      const groupFeeItems = this.generateGroupFeeItems(
        groupId,
        billingQuantity, 
        groupStats, 
        products
      );
      console.log(`[PVC Processor] 📋 group${groupId}生成${groupFeeItems.length}个费用项:`, groupFeeItems);
      
      allFeeItems.push(...groupFeeItems);
    });
    
    console.log(`[PVC Processor] ✅ 所有group处理完成，总共生成${allFeeItems.length}个费用项:`, allFeeItems);
    
    // 检查每个费用项的关键数据
    allFeeItems.forEach((item, index) => {
      console.log(`[PVC Processor] 费用项${index + 1}:`, {
        variantId: item.id,
        quantity: item.quantity,
        side: item.properties._custom_side,
        groupId: item.properties._group_id,
        description: item.properties._description
      });
    });
    
    return allFeeItems;
  }
  
  /**
   * 按group_id分组
   */
  groupByGroupId(customProducts) {
    const groups = {};
    
    customProducts.forEach(product => {
      const groupId = product.groupId; // Manager已经解析好的groupId
      if (!groups[groupId]) {
        groups[groupId] = [];
      }
      groups[groupId].push(product);
    });
    
    return groups;
  }
  
  /**
   * 计算单个group的统计信息
   */
  calculateGroupStats(groupProducts) {
    let totalQuantity = 0;
    const allCustomSides = new Set();
    const productDetails = [];
    let draftOrderId = null;
    
    groupProducts.forEach(item => {
      totalQuantity += item.quantity;
      
      // 获取草稿单ID（用于后续属性标记）
      if (!draftOrderId) {
        draftOrderId = item.draftOrderId;
      }
      
      // 收集定制面信息
      console.log(`[PVC Processor] 🔍 处理商品定制面:`, {
        customSides: item.customSides,
        customSidesType: typeof item.customSides,
        isArray: Array.isArray(item.customSides)
      });
      
      if (item.customSides && Array.isArray(item.customSides)) {
        item.customSides.forEach(side => {
          console.log(`[PVC Processor] 添加定制面: ${side}`);
          allCustomSides.add(side);
        });
      }
      
      // 记录产品详情
      productDetails.push({
        quantity: item.quantity,
        customSides: item.customSides || [],
        draftOrderId: item.draftOrderId,
        groupId: item.groupId,
        productTitle: item.product?.title || 'Unknown Product'
      });
    });
    
    console.log(`[PVC Processor] 📊 统计结果:`, {
      totalQuantity,
      uniqueCustomSides: Array.from(allCustomSides),
      customSideCount: allCustomSides.size,
      allCustomSidesSet: allCustomSides
    });
    
    return {
      totalQuantity,
      uniqueCustomSides: Array.from(allCustomSides),
      customSideCount: allCustomSides.size,
      productDetails,
      draftOrderId // 保留草稿单ID用于属性标记
    };
  }
  
  /**
   * 为单个group生成费用项
   */
  generateGroupFeeItems(groupId, billingQuantity, groupStats, groupProducts) {
    const feeItems = [];
    const { uniqueCustomSides, productDetails, draftOrderId, totalQuantity } = groupStats;
    
    // PVC工艺的Plate Fee始终存在，不受数量限制
    const plateFeeItem = this.createPlateFeeItem(totalQuantity, groupId, draftOrderId);
    if (plateFeeItem) {
      feeItems.push(plateFeeItem);
      console.log(`[PVC Processor] 🛠️ group${groupId}添加Plate Fee:`, plateFeeItem);
    }
    
    // 为该group的每个定制面生成一个费用项
    uniqueCustomSides.forEach((customSide, index) => {
      const feeItem = this.createPVCFeeItem(
        groupId,
        draftOrderId,
        billingQuantity, 
        customSide, 
        index,
        productDetails,
        totalQuantity // 传递实际数量用于设置费用项数量
      );
      feeItems.push(feeItem);
    });
    
    return feeItems;
  }
  
  /**
   * 计算实际计费数量（PVC第一个区间有最小起订量）
   */
  calculateBillingQuantity(actualQuantity, priceRange) {
    // PVC工艺：第一个区间（1-11）最小收费按11个计算
    if (priceRange.min === 1 && actualQuantity < 11) {
      return 11; // 最小起订量
    }
    return actualQuantity;
  }
  
  /**
   * 创建PVC Plate Fee项
   */
  createPlateFeeItem(quantity, groupId, draftOrderId) {
    return {
      id: 46024052834457, // Patch Digitizing Fee 变体ID
      quantity: 1, // Plate Fee 始终是1个，不跟随主商品数量
      properties: {
        // 用户可见的属性（会在购物车页面显示）
        'Craft Type': 'PVC Patch',
        'Description': 'One-time plate fee for PVC patch',
        
        // 内部管理属性（以下划线开头，不显示在购物车）
        '_custom_fee_type': 'PVC_PATCH',
        '_plate_fee': 'true',
        '_setup_fee': 'true', // 兼容性标记
        '_group_id': groupId, // 关键：group ID（主要分组标识）
        '_draft_order_id': draftOrderId, // 草稿单ID（用于兼容和追踪）
        '_billing_quantity': quantity,
        '_actual_quantity': 1, // Plate Fee 数量始终是1
        '_description': `PVC Patch Plate Fee (applies to ${quantity} pcs)`,
        '_pvc_specific': 'true',
        '_patch_type': 'PVC Patch',
        '_is_plate_fee': 'true', // 标识这是Plate Fee
        '_always_required': 'true' // 标识这个费用始终需要
      }
    };
  }

  /**
   * 创建单个PVC费用项
   */
  createPVCFeeItem(groupId, draftOrderId, billingQuantity, customSide, sideIndex, productDetails, actualQuantity) {
    const variantId = this.getVariantIdByQuantity(billingQuantity);
    
    // 计算定制费数量：主商品数量 × 定制面数量（这个定制面只算1个面）
    // 每个面都会调用这个方法，所以每个费用项的数量就是主商品的总数量
    const customFeeQuantity = actualQuantity; // actualQuantity 已经是所有主商品的总数量
    
    // 生成详细描述
    const description = this.generatePVCDescription(customSide, billingQuantity, productDetails, customFeeQuantity);
    const sideDisplayName = this.getCustomSideDisplayName(customSide);
    
    // 构建定制费商品的可见属性
    const visibleProperties = {
      // 用户可见的属性（会在购物车页面显示）
      'Custom Side': sideDisplayName,
      'Craft Type': 'PVC Patch', 
      'Products': this.generateProductsSummary(productDetails),
      
      // 内部管理属性（以下划线开头，不显示在购物车）
      '_custom_fee_type': 'PVC_PATCH',
      '_group_id': groupId, // 关键：group ID（主要分组标识）
      '_draft_order_id': draftOrderId, // 草稿单ID（用于兼容和追踪）
      '_custom_side': customSide,
      '_billing_quantity': billingQuantity,
      '_actual_quantity': actualQuantity, // 记录实际商品数量
      '_custom_fee_quantity': customFeeQuantity, // 记录定制费数量
      '_description': description,
      '_side_index': sideIndex + 1,
      '_total_sides': productDetails.reduce((sum, p) => sum + p.customSides.length, 0),
      '_products_summary': this.generateProductsSummary(productDetails),
      '_pvc_specific': 'true',
      '_patch_type': 'PVC Patch',
      '_is_custom_fee': 'true' // 标识这是定制费商品
    };
    
    return {
      id: variantId,
      quantity: customFeeQuantity, // 定制费数量 = 主商品数量（每个面都会有一个费用项）
      properties: visibleProperties
    };
  }
  
  /**
   * 生成PVC费用项描述
   */
  generatePVCDescription(customSide, billingQuantity, productDetails, customFeeQuantity = null) {
    const sideDisplayName = this.getCustomSideDisplayName(customSide);
    const productCount = productDetails.length;
    const totalActualQty = productDetails.reduce((sum, p) => sum + p.quantity, 0);
    const groupId = productDetails[0]?.groupId || 'Unknown';
    
    let description = `PVC Patch Custom Fee - ${sideDisplayName}`;
    
    // 添加定制费数量信息
    if (customFeeQuantity !== null) {
      description += ` (${customFeeQuantity} custom fees for ${totalActualQty} products)`;
      
      // 如果有最小计费量差异，也显示出来
      if (billingQuantity !== totalActualQty) {
        description += ` [minimum charge: ${billingQuantity} pcs]`;
      }
    } else {
      // 保持原有逻辑作为备用
      if (billingQuantity !== totalActualQty) {
        description += ` (${totalActualQty} pcs actual, ${billingQuantity} pcs minimum charge)`;
      } else {
        description += ` (${billingQuantity} pcs)`;
      }
    }
    
    // 添加商品信息
    if (productCount === 1) {
      description += ` | Product: ${productDetails[0].productTitle}`;
    } else {
      description += ` | ${productCount} mixed products`;
    }
    
    // 添加group信息
    description += ` | Group: ${groupId}`;
    
    return description;
  }
  
  /**
   * 生成商品汇总信息
   */
  generateProductsSummary(productDetails) {
    return productDetails.map(p => 
      `${p.productTitle} (${p.quantity} pcs)`
    ).join(', ');
  }
  
  /**
   * PVC工艺特有的验证逻辑
   */
  validatePVCSpecificRules(products) {
    // 验证PVC工艺的特殊要求
    for (const product of products) {
      // 检查是否有定制面
      if (!product.customSides || product.customSides.length === 0) {
        console.warn('[PVC Processor] 商品缺少定制面信息:', product);
        return false;
      }
      
      // PVC工艺可能的材质或尺寸限制
      if (product.quantity <= 0) {
        console.warn('[PVC Processor] 商品数量无效:', product);
        return false;
      }
    }
    
    return true;
  }
}

// 将处理器暴露到全局作用域
window.PVCProcessor = PVCProcessor;
